import { autorun, extras } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AuthenticationState, Context } from 'bernie-context';

import { AdvertisingStore } from 'stores/advertising-store';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { SearchResultsStore } from 'stores/search-results-store';

import { PageName } from 'constants/pages';
import { IntentMediaModel } from 'models/common/ads-types';
import { ErrorBoundary } from 'bernie-view';

interface IntentMediaProps {
  advertising?: AdvertisingStore;
  context?: Context;
  currentHotel?: CurrentHotelStore;
  searchCriteria: SearchCriteriaModel;
  searchResults?: SearchResultsStore;
}

@inject('context', 'currentHotel', 'searchResults', 'advertising')
@observer
export class IntentMedia extends React.Component<IntentMediaProps> {
  private halt: () => any;

  constructor(props) {
    super(props);

    this.isValid = this.isValid.bind(this);
    this.mapContextData = this.mapContextData.bind(this);
    this.mapCurrentHotelData = this.mapCurrentHotelData.bind(this);
    this.mapPropertySearchData = this.mapPropertySearchData.bind(this);
    this.mapReferrerData = this.mapReferrerData.bind(this);
    this.mapSearchCriteriaData = this.mapSearchCriteriaData.bind(this);
    this.mapSharedGuidsData = this.mapSharedGuidsData.bind(this);
  }

  private isValid(intentMediaProperties, isInfosite: boolean) {
    const { publisher_hotel_property_name, publisher_hotel_property_id, hotel_city, hotel_state, hotel_country } = intentMediaProperties;

    if (isInfosite && !(publisher_hotel_property_name && publisher_hotel_property_id)) {
      return false;
    }

    if (!hotel_city && !hotel_state && !hotel_country) {
      return false;
    }

    return true;
  }

  private mapContextData(context: Context) {
    const {
      currency,
      deviceInformation,
      user: { authState, expUserId, isRegistered }
    } = context;

    let displayFormatType = 'Desktop';
    if (deviceInformation) {
      if (deviceInformation.tablet) {
        displayFormatType = 'Tablet';
      } else if (deviceInformation.mobile) {
        displayFormatType = 'Mobile';
      }
    }

    return {
      display_format_type: displayFormatType,
      is_logged_in_user: authState === AuthenticationState.ANONYMOUS ? 'N' : 'Y',
      is_member: isRegistered ? 'Y' : 'N',
      site_currency: currency || '',
      user_member_id: expUserId && expUserId !== -1 ? expUserId.toString() : ''
    };
  }

  private mapCurrentHotelData(currentProperties, currentHotel: CurrentHotelStore, isInfosite: boolean) {
    if (!isInfosite) {
      return {
        publisher_hotel_property_id: '',
        publisher_hotel_property_name: ''
      };
    }

    const { publisher_hotel_property_name: prevHotelName = '', publisher_hotel_property_id: prevHotelId = '' } = currentProperties;
    const { hotel } = currentHotel;
    const nextHotelName = hotel && hotel.hotelName;
    const nextHotelId = hotel && hotel.hotelId;
    const bothUpdated = prevHotelName !== nextHotelName && prevHotelId !== nextHotelId;

    return {
      publisher_hotel_property_id: bothUpdated ? nextHotelId : prevHotelId,
      publisher_hotel_property_name: bothUpdated ? nextHotelName : prevHotelName
    };
  }

  private mapPropertySearchData(
    searchCriteria: SearchCriteriaModel,
    searchResults: SearchResultsStore,
    currentHotel: CurrentHotelStore,
    isInfosite: boolean
  ) {
    if ((isInfosite && currentHotel && currentHotel.regionInfo) || (!isInfosite && searchResults && searchResults.regionInfo)) {
      const { city = '', state = '', countryCode = '' } = isInfosite ? currentHotel.regionInfo : searchResults.regionInfo;

      return {
        hotel_city: city,
        hotel_country: countryCode,
        hotel_rooms: searchCriteria.rooms.length,
        hotel_state: state
      };
    }
  }

  private mapReferrerData(searchResults: SearchResultsStore, currentHotel: CurrentHotelStore, isInfosite: boolean) {
    if ((isInfosite && currentHotel && currentHotel.referrerInfo) || (!isInfosite && searchResults && searchResults.referrerInfo)) {
      const { referrerChannel, referrerSource } = isInfosite ? currentHotel.referrerInfo : searchResults.referrerInfo;

      return {
        referrer_channel: referrerChannel,
        referrer_source: referrerSource
      };
    }

    return {
      referrer_channel: '',
      referrer_source: ''
    };
  }

  private mapSearchCriteriaData(searchCriteria: SearchCriteriaModel) {
    return {
      adults: searchCriteria.adults,
      children: searchCriteria.children,
      travel_date_end: searchCriteria.endDate ? searchCriteria.endDate.replace(/-/g, '') : '',
      travel_date_start: searchCriteria.startDate ? searchCriteria.startDate.replace(/-/g, '') : '',
      travelers: searchCriteria.guestCount
    };
  }

  private mapSharedGuidsData(context: Context, currentHotel: CurrentHotelStore, isInfosite: boolean, searchResults: SearchResultsStore) {
    if ((isInfosite && currentHotel && currentHotel.sharedGuids) || (!isInfosite && searchResults && searchResults.sharedGuids)) {
      const {
        machineId = '',
        machineIdHashed = '',
        publisherSessionId = '',
        publisherSessionIdHashed = '',
        publisherUserIdHashed = ''
      } = isInfosite ? currentHotel.sharedGuids : searchResults.sharedGuids;

      return {
        machine_id_hashed: machineIdHashed,
        machine_id: machineId,
        publisher_session_id_hashed: publisherSessionIdHashed,
        publisher_session_id: publisherSessionId,
        publisher_user_id_hashed: publisherUserIdHashed,
        publisher_user_id: context.deviceId ? context.deviceId.toUpperCase() : ''
      };
    }
  }

  public componentDidMount() {
    let intentMediaData: IntentMediaModel = {};
    window.meso = window.meso || {};
    window.meso.adcontext = window.meso.adcontext || [];

    this.halt = autorun(() => {
      const { advertising, context, currentHotel, searchCriteria, searchResults } = this.props;

      const isInfosite: boolean = advertising.triggeredPageName === PageName.INFOSITE;

      const referrerData = this.mapReferrerData(searchResults, currentHotel, isInfosite);
      const searchCriteriaData = this.mapSearchCriteriaData(searchCriteria);
      const contextData = this.mapContextData(context);
      const sharedGuidsData = this.mapSharedGuidsData(context, currentHotel, isInfosite, searchResults);
      const currentHotelData = this.mapCurrentHotelData(intentMediaData, currentHotel, isInfosite);
      const propertySearchData = this.mapPropertySearchData(searchCriteria, searchResults, currentHotel, isInfosite);
      const updatedIntentMediaData = {
        ...contextData,
        ...currentHotelData,
        ...propertySearchData,
        ...referrerData,
        ...searchCriteriaData,
        ...sharedGuidsData
      };

      if (this.isValid(updatedIntentMediaData, isInfosite) && !extras.deepEqual(intentMediaData, updatedIntentMediaData)) {
        window.meso.adcontext.push(updatedIntentMediaData);
        intentMediaData = updatedIntentMediaData;
      }
    });
  }

  public componentWillUnmount() {
    if (typeof this.halt === 'function') {
      this.halt();
    }
  }

  public render() {
    return (
      <ErrorBoundary label="meso.intent.media">
        <meso-intent-media />
      </ErrorBoundary>
    );
  }
}
